import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { Box, Chip, Paper, Tab, Tabs, Typography } from '@mui/material';
import * as searchInvoiceMetaData from '../../actions/invoiceDateActions';
import SystemNotesTable from './SystemNotesTable';
import { Section, Field, FieldGrid } from '../../components/common/Section';
import { tokens } from '../../theme';

const TIMELINE = [
    ['Request received', 'requestTime'],
    ['Dispatched', 'dispatchTime'],
    ['Accepted', 'acceptedTime'],
    ['En route', 'enrouteTime'],
    ['Arrived', 'arrivedTime'],
    ['Hooked', 'hookedTime'],
    ['Dropped off', 'dropOffTime'],
    ['Cleared', 'clearedTime'],
];

const Timeline = ({ invoice }) => (
    <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {TIMELINE.map(([label, key], index) => {
            const time = invoice[key];
            const done = Boolean(time);
            const last = index === TIMELINE.length - 1;
            return (
                <Box component="li" key={key} sx={{ display: 'flex', gap: 1.5, position: 'relative', pb: last ? 0 : 2 }}>
                    {!last && (
                        <Box
                            sx={{
                                position: 'absolute',
                                left: 5,
                                top: 14,
                                bottom: 0,
                                width: 2,
                                bgcolor: done ? tokens.blue : tokens.line,
                            }}
                        />
                    )}
                    <Box
                        sx={{
                            width: 12,
                            height: 12,
                            mt: '4px',
                            borderRadius: '50%',
                            flexShrink: 0,
                            bgcolor: done ? tokens.blue : 'background.paper',
                            border: `2px solid ${done ? tokens.blue : tokens.line}`,
                            zIndex: 1,
                        }}
                    />
                    <Box>
                        <Typography sx={{ fontWeight: 500, color: done ? 'text.primary' : 'text.secondary' }}>{label}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {time || 'Not yet'}
                        </Typography>
                    </Box>
                </Box>
            );
        })}
    </Box>
);

const VendorInvoiceView = ({ fetchInvoice, invoiceDetails, saveNotes, invoicenotes, match }) => {
    const [activeTab, setActiveTab] = useState('details');
    const invoiceId = match.params.invoiceId;

    useEffect(() => {
        if (invoiceId) fetchInvoice(invoiceId);
    }, [invoiceId, fetchInvoice]);

    const handleSaveNotes = (obj) => saveNotes(obj, invoiceId);

    const inv = invoiceDetails || {};
    const released = inv.releaseStatus && inv.releaseStatus.toLowerCase() === 'released';

    return (
        <Box sx={{ maxWidth: 1280 }}>
            <Paper variant="outlined" sx={{ mb: 2 }}>
                <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ flex: 1, minWidth: 220 }}>
                        <Typography variant="h3" component="h2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            Invoice {inv.invoiceId || invoiceId}
                        </Typography>
                        <Typography color="text.secondary" sx={{ mt: 0.25 }}>
                            {inv.name || ''}
                            {inv.invoiceCreatedDate ? `${inv.name ? ', ' : ''}created ${inv.invoiceCreatedDate}` : ''}
                        </Typography>
                    </Box>
                    {inv.callStatus && <Chip label={inv.callStatus} color="primary" variant="outlined" />}
                    {inv.releaseStatus && (
                        <Chip label={inv.releaseStatus} color={released ? 'success' : 'warning'} variant="outlined" />
                    )}
                </Box>
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ px: 1.5 }}>
                    <Tab label="Details" value="details" />
                    <Tab label={`Notes${invoicenotes && invoicenotes.length ? ` (${invoicenotes.length})` : ''}`} value="notes" />
                </Tabs>
            </Paper>

            {activeTab === 'details' ? (
                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 2fr) minmax(0, 1fr)' } }}>
                    <Box sx={{ display: 'grid', gap: 2, alignContent: 'start' }}>
                        <Section title="Service">
                            <FieldGrid>
                                <Field label="Account" value={inv.name} />
                                <Field label="Contact" value={inv.callerName} />
                                <Field label="Reason" value={inv.reason} />
                                <Field label="Rate plan" value={inv.accountPlanRateName} />
                            </FieldGrid>
                        </Section>
                        <Section title="Vehicle">
                            <FieldGrid columns={3}>
                                <Field label="Year" value={inv.vehicle_Year} />
                                <Field label="Make" value={inv.vehicle_Make} />
                                <Field label="Model" value={inv.vehicle_Model} />
                                <Field label="Color" value={inv.vehicle_Color} />
                                <Field label="Plate" value={inv.vehicle_Plate_No} mono />
                                <Field label="OR/DR #" value={inv.vehicle_OR_DR_No} mono />
                            </FieldGrid>
                            <Box sx={{ mt: 2.5 }}>
                                <Field label="VIN" value={inv.vehicle_VIN} mono />
                            </Box>
                        </Section>
                        <Section title="Locations">
                            <FieldGrid>
                                <Field label="Pickup" value={inv.pickUpAddress} />
                                <Field label="Drop off" value={inv.dropOffAddress} />
                            </FieldGrid>
                        </Section>
                    </Box>

                    <Box sx={{ display: 'grid', gap: 2, alignContent: 'start' }}>
                        {inv.releaseStatus && (
                            <Section title="Release">
                                <Box sx={{ display: 'grid', gap: 2 }}>
                                    <Field label="Lot" value={inv.dropOffLocation} />
                                    {released && <Field label="Released on" value={inv.releaseDateTime} />}
                                    {released && <Field label="Released by" value={inv.releasedName} />}
                                    {released && <Field label="Released to" value={inv.releasedTo} />}
                                </Box>
                            </Section>
                        )}
                        <Section title="Timeline">
                            <Timeline invoice={inv} />
                        </Section>
                    </Box>
                </Box>
            ) : (
                <Section title="Notes">
                    <SystemNotesTable notesData={invoicenotes} invoiceId={invoiceId} saveNotes={handleSaveNotes} />
                </Section>
            )}
        </Box>
    );
};

const mapStateToProps = (state) => ({
    invoiceDetails: (state.invoiceList && state.invoiceList.invoiceDetails) || {},
    invoicenotes: (state.invoiceList && state.invoiceList.invoicenotes) || [],
    authInfo: state.authInfo,
});

const mapDispatchToProps = (dispatch) => ({
    fetchInvoice: (id) => {
        dispatch(searchInvoiceMetaData.fetchInvoice(id));
        dispatch(searchInvoiceMetaData.fetchInvoiceNotes(id));
    },
    saveNotes: async (obj, id) => {
        await dispatch(searchInvoiceMetaData.saveNotes(obj, id));
    },
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(VendorInvoiceView));
